import { type CheckResult } from "../../../../models/audit/auditSession";
import { getIssueDisplayStatus } from "../utils/issueDisplayStatus";

/**
 * What a verdict cell reads as where a surface marks it with a single result
 * rather than rendering the cell itself.
 *
 * `pending` is the state of a cell holding no verdict at all, and it is the only
 * member no verdict maps to — a cell reaches it by having nothing, never by
 * carrying an answer a surface could not classify.
 */
export type VerdictCellResult =
  | "pass"
  | "fail"
  | "not_applicable"
  | "inconclusive"
  | "pending";

/**
 * The badge an analytics verdict amounts to, read off the `CheckResult` the
 * translator mints rather than off the verdict's own `outcome`/`isConclusive`.
 *
 * `CheckDecision` badges an analytics cell from the same `getIssueDisplayStatus`
 * status, so a mark derived here agrees with the cell's badge as that vocabulary
 * moves. Deriving the mark from the verdict directly would be a second outcome
 * mapping to keep in step with the first, and the copies would disagree on the
 * states that are easiest to get wrong: an inconclusive verdict and a pass
 * carrying exceptions.
 */
export function analyticsCellResult(
  checkResult: CheckResult,
): VerdictCellResult {
  switch (getIssueDisplayStatus(checkResult.issues)) {
    case "no_issues":
      return "pass";
    case "na":
      return "not_applicable";
    case "requires_attention":
      return "inconclusive";
    case "issues_found":
      return "fail";
  }
}
